import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import logoFull from "@/assets/logo-full-white.png";
import ParticleText from "@/components/ParticleText";

const ANIMATION_SCROLL_HEIGHT = 2000; // px of scroll consumed by full animation

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [heroSize, setHeroSize] = useState({ w: 0, h: 0 });
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const p = Math.min(1, Math.max(0, scrollTop / ANIMATION_SCROLL_HEIGHT));
      setProgress(p);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const p = Math.min(1, Math.max(0, scrollTop / ANIMATION_SCROLL_HEIGHT));
      setProgress(p);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track hero size for canvas
  useEffect(() => {
    const updateSize = () => {
      setHeroSize({ w: window.innerWidth, h: window.innerHeight });
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Phase breakdown (within progress 0-1):
  // 0.00 - 0.15: Logo visible, starts fading out
  // 0.10 - 0.35: Particles assemble into text (ParticleText progress 0→0.5)
  // 0.35 - 0.60: HOLD — text perfectly formed, readable (ParticleText stays at 0.5)
  // 0.60 - 1.00: Particles explode outward (ParticleText progress 0.5→1.0)

  const logoOpacity = Math.max(0, 1 - progress / 0.15);
  const logoTranslateY = -progress * 300;
  const logoScale = Math.max(0.5, 1 - progress * 0.5);

  let particleProgress: number;
  if (progress < 0.10) {
    particleProgress = 0;
  } else if (progress < 0.35) {
    // Assembling: map 0.10-0.35 → 0-0.5
    particleProgress = ((progress - 0.10) / 0.25) * 0.5;
  } else if (progress < 0.60) {
    // HOLD: text fully formed for 25% of scroll range (~500px of scroll)
    particleProgress = 0.5;
  } else {
    // Exploding: map 0.60-1.0 → 0.5-1.0
    particleProgress = 0.5 + ((progress - 0.60) / 0.40) * 0.5;
  }

  return (
    <div className="relative" style={{ height: `${ANIMATION_SCROLL_HEIGHT + heroSize.h}px` }}>
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Native Background Video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            ref={videoRef}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto object-cover pointer-events-none"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            src="/hero-bg.mp4"
          />
          <div className="absolute inset-0 bg-background/60" />
        </div>

        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full bg-primary/15 blur-[150px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />

        {/* Logo - fades out first */}
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          style={{
            transform: `translateY(${logoTranslateY}px) scale(${logoScale})`,
            opacity: logoOpacity,
            pointerEvents: "none",
          }}
        >
          <img
            src={logoFull}
            alt="Freela Media"
            className="w-[320px] sm:w-[420px] md:w-[520px] lg:w-[620px] drop-shadow-[0_0_40px_hsla(199,76%,52%,0.4)]"
          />
        </div>

        {/* Particle Text Canvas */}
        <div className="absolute inset-0 z-20 pointer-events-none">
          <ParticleText
            progress={particleProgress}
            width={heroSize.w}
            height={heroSize.h}
            textPart1={t("hero.particlePart1")}
            textPart2={t("hero.particlePart2")}
            multiLine={heroSize.w < 640}
          />
        </div>

        {/* Dark overlay that fades in as animation ends, to transition to content */}
        <div
          className="absolute inset-0 z-30 pointer-events-none"
          style={{
            backgroundColor: `hsl(214 100% 5% / ${progress >= 0.85 ? Math.min(1, (progress - 0.85) / 0.15) : 0})`,
          }}
        />
      </div>
    </div>
  );
};

export default HeroSection;
